// Fichier: src/pages/Payments.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, updateDoc, doc, getDoc, addDoc } from 'firebase/firestore';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Grid
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as PaidIcon,
  Cancel as UnpaidIcon,
  Visibility as ViewIcon,
  Euro as EuroIcon,
  AttachMoney as ReconciliationIcon,
  CloudUpload as ImportIcon,
  Payment as PaymentIcon,
  Build as CustomIcon,
  MarkEmailRead as ReminderIcon
} from '@mui/icons-material';
import moment from 'moment';
import * as XLSX from 'xlsx';

// Coordonnées bancaires de l'école, reprises dans les emails de rappel de paiement
const BANK_INFO = {
  beneficiary: 'Beunit asbl (MKD Studio)',
  iban: 'BE80 7340 4589 7577',
  bic: 'KREDBEBBXXX'
};

const Payments = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');
  const [reconciliationDialogOpen, setReconciliationDialogOpen] = useState(false);
  const [manualPaymentDialogOpen, setManualPaymentDialogOpen] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [bankTransactions, setBankTransactions] = useState([]);
  const [matchedTransactions, setMatchedTransactions] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [manualPaymentData, setManualPaymentData] = useState({
    paymentDate: moment().format('YYYY-MM-DD'),
    bankReference: '',
    notes: ''
  });

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return moment(date.toDate()).format('DD/MM/YYYY HH:mm');
    } catch (error) {
      return 'Format invalide';
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const registrationsRef = collection(db, 'registrations');
      const q = query(registrationsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const paymentsData = [];
      for (const doc of querySnapshot.docs) {
        const data = doc.data();
        
        // Récupérer les informations des étudiants liés
        let studentsInfo = [];
        if (data.persons && data.persons.length > 0) {
          for (const person of data.persons) {
            if (person.studentId) {
              try {
                const studentDoc = await getDoc(doc(db, 'students', person.studentId));
                if (studentDoc.exists()) {
                  studentsInfo.push({
                    ...studentDoc.data(),
                    id: studentDoc.id
                  });
                }
              } catch (error) {
                console.error('Erreur récupération étudiant:', error);
              }
            }
          }
        }

        // Déterminer le statut de paiement
        const isPaid = data.paymentStatus === 'paid';
        const paymentStatus = isPaid ? 'Payé' : 'En attente';
        const totalAmount = data.paymentDetails?.totalAmount || 0;

        // Ne pas inclure les formules d'essai (même avec montant > 0)
        if (totalAmount > 0 && data.plan !== 'essai') {
          // Déterminer le nom de la formule à afficher
          let displayPlanName = data.planName || '';
          if (data.planType === 'custom' && data.customPlanName) {
            displayPlanName = data.customPlanName;
          }

          paymentsData.push({
            id: doc.id,
            ...data,
            studentsInfo,
            createdAt: data.createdAt,
            selectedDate: data.selectedDate,
            personsCount: data.persons?.length || 0,
            personsInfo: data.persons?.map(p => `${p.firstName} ${p.lastName}`).join(', ') || '',
            contactInfo: `${data.contactInfo?.email || ''}${data.contactInfo?.phone ? ` (${data.contactInfo.phone})` : ''}`,
            email: data.contactInfo?.email || '',
            parentContact: data.parentInfo ? 
              `${data.parentInfo.parentFirstName} ${data.parentInfo.parentLastName}` : 
              'N/A',
            type: data.registrationType === 'child' ? 'Enfant' : 'Adulte',
            insurance: data.insuranceIncluded ? 'Incluse' : 'Non',
            paymentStatus,
            isPaid,
            totalAmount,
            paymentMethod: data.paymentDetails?.paymentMethod || 'bank',
            customCommunication: data.paymentDetails?.customCommunication || '',
            planType: data.planType || 'formula',
            displayPlanName: displayPlanName,
            reference: `INS-${doc.id.slice(-8).toUpperCase()}`
          });
        }
      }
      
      setPayments(paymentsData);
    } catch (error) {
      console.error("Erreur de chargement des paiements:", error);
      showSnackbar('Erreur de chargement des paiements', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const handleMarkAsPaid = async (paymentId, bankReference = '', paymentDate = new Date(), notes = '') => {
    try {
      const paymentRef = doc(db, 'registrations', paymentId);
      await updateDoc(paymentRef, {
        paymentStatus: 'paid',
        paidAt: paymentDate ? new Date(paymentDate) : new Date(),
        bankReference: bankReference || `MAN-${moment().format('YYYYMMDD-HHmm')}`,
        paymentNotes: notes
      });
      fetchPayments();
      showSnackbar('Paiement marqué comme payé');
      setManualPaymentDialogOpen(false);
      setManualPaymentData({
        paymentDate: moment().format('YYYY-MM-DD'),
        bankReference: '',
        notes: ''
      });
    } catch (error) {
      console.error("Erreur lors du marquage comme payé:", error);
      showSnackbar('Erreur lors du marquage comme payé', 'error');
    }
  };

  const handleManualPayment = (payment) => {
    setSelectedPayment(payment);
    setManualPaymentDialogOpen(true);
  };

  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPayment(null);
  };

  const handleCloseManualPaymentDialog = () => {
    setManualPaymentDialogOpen(false);
    setSelectedPayment(null);
    setManualPaymentData({
      paymentDate: moment().format('YYYY-MM-DD'),
      bankReference: '',
      notes: ''
    });
  };

  const handleReconciliation = () => {
    setReconciliationDialogOpen(true);
  };

  const handleOpenReminder = (payment) => {
    setSelectedPayment(payment);
    setReminderDialogOpen(true);
  };

  const handleCloseReminderDialog = () => {
    setReminderDialogOpen(false);
    setSelectedPayment(null);
  };

  const handleSendReminder = async () => {
    if (!selectedPayment) return;
    if (!selectedPayment.email) {
      showSnackbar("Aucune adresse email n'est associée à ce paiement", 'error');
      return;
    }

    setSendingReminder(true);
    try {
      const communication = selectedPayment.customCommunication || selectedPayment.reference;

      await addDoc(collection(db, 'mail'), {
        to: [selectedPayment.email],
        message: {
          subject: 'Rappel de paiement - MKD Studio',
          html: `
            <p>Bonjour,</p>
            <p>Nous n'avons pas encore reçu le paiement concernant l'inscription de <strong>${selectedPayment.personsInfo}</strong> (${selectedPayment.displayPlanName}).</p>
            <p><strong>Montant à payer : ${selectedPayment.totalAmount}€</strong></p>
            <p>Merci de bien vouloir effectuer le virement dans les meilleurs délais, en indiquant impérativement la communication ci-dessous afin que nous puissions identifier votre paiement :</p>
            <table style="margin: 12px 0;">
              <tr><td style="padding: 2px 12px 2px 0;"><strong>Bénéficiaire</strong></td><td>${BANK_INFO.beneficiary}</td></tr>
              <tr><td style="padding: 2px 12px 2px 0;"><strong>IBAN</strong></td><td>${BANK_INFO.iban}</td></tr>
              <tr><td style="padding: 2px 12px 2px 0;"><strong>BIC</strong></td><td>${BANK_INFO.bic}</td></tr>
              <tr><td style="padding: 2px 12px 2px 0;"><strong>Montant</strong></td><td>${selectedPayment.totalAmount}€</td></tr>
              <tr><td style="padding: 2px 12px 2px 0;"><strong>Communication</strong></td><td>${communication}</td></tr>
            </table>
            <p>Si vous avez déjà effectué ce paiement, merci d'ignorer ce message.</p>
            <p>Belle journée,<br/>MKD Studio</p>
          `
        }
      });

      showSnackbar('Email de rappel envoyé avec succès');
      setReminderDialogOpen(false);
      setSelectedPayment(null);
    } catch (error) {
      console.error("Erreur lors de l'envoi du rappel:", error);
      showSnackbar("Erreur lors de l'envoi de l'email de rappel", 'error');
    } finally {
      setSendingReminder(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
        
        // Traitement des données Excel (à adapter selon le format de votre banque)
        const transactions = jsonData.slice(1).map(row => ({
          date: row[0],
          description: row[1],
          amount: row[2],
          reference: row[3]
        }));
        
        setBankTransactions(transactions);
        matchTransactions(transactions);
      } catch (error) {
        console.error('Erreur de lecture du fichier:', error);
        showSnackbar('Erreur de lecture du fichier', 'error');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const matchTransactions = (transactions) => {
    const matched = [];
    transactions.forEach(transaction => {
      const amount = parseFloat(transaction.amount);
      const pendingPayments = payments.filter(p => !p.isPaid);
      
      const match = pendingPayments.find(payment => 
        Math.abs(payment.totalAmount - amount) < 0.01 || // Correspondance par montant
        transaction.description?.includes(payment.reference) || // Correspondance par référence
        transaction.description?.includes(payment.customCommunication) // Correspondance par communication
      );
      
      if (match) {
        matched.push({
          transaction,
          payment: match
        });
      }
    });
    
    setMatchedTransactions(matched);
  };

  const handleAutoReconcile = () => {
    matchedTransactions.forEach(async (match) => {
      await handleMarkAsPaid(
        match.payment.id,
        match.transaction.reference || `AUTO-${moment().format('YYYYMMDD')}`,
        match.transaction.date,
        'Réconcilié automatiquement'
      );
    });
    setReconciliationDialogOpen(false);
    showSnackbar(`${matchedTransactions.length} paiements réconciliés automatiquement`);
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = 
      payment.personsInfo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.contactInfo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.customCommunication.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.displayPlanName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'paid' && payment.isPaid) ||
      (filterStatus === 'pending' && !payment.isPaid);
    
    const matchesPlan = filterPlan === 'all' || 
      payment.planType === filterPlan;

    return matchesSearch && matchesStatus && matchesPlan;
  });

  const paginatedPayments = filteredPayments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getStatusChip = (isPaid) => {
    return isPaid ? (
      <Chip 
        icon={<PaidIcon />} 
        label="Payé" 
        color="success" 
        variant="outlined" 
        size="small" 
      />
    ) : (
      <Chip 
        icon={<UnpaidIcon />} 
        label="En attente" 
        color="warning" 
        variant="outlined" 
        size="small" 
      />
    );
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'bank':
        return <PaymentIcon color="primary" />;
      case 'cash':
        return <EuroIcon color="success" />;
      case 'check':
        return <PaymentIcon color="secondary" />;
      case 'card':
        return <PaymentIcon color="info" />;
      default:
        return <PaymentIcon />;
    }
  };

  const getPlanTypeIcon = (planType) => {
    return planType === 'custom' ? 
      <CustomIcon color="secondary" /> : 
      <PaymentIcon color="primary" />;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Gestion des Paiements
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ReconciliationIcon />}
            onClick={handleReconciliation}
          >
            Réconciliation
          </Button>
          <Button
            variant="contained"
            startIcon={<PaymentIcon />}
            onClick={() => navigate('/payments/new')}
          >
            Nouveau Paiement
          </Button>
        </Box>
      </Box>

      {/* Filtres et recherche */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Rechercher par nom, email, communication..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Statut</InputLabel>
              <Select
                value={filterStatus}
                label="Statut"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="paid">Payés</MenuItem>
                <MenuItem value="pending">En attente</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={filterPlan}
                label="Type"
                onChange={(e) => setFilterPlan(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="formula">Formules</MenuItem>
                <MenuItem value="custom">Personnalisés</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Tooltip title="Actualiser">
                <IconButton onClick={fetchPayments} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tableau des paiements */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Élève(s)</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Formule/Montant</strong></TableCell>
              <TableCell><strong>Montant</strong></TableCell>
              <TableCell><strong>Communication</strong></TableCell>
              <TableCell><strong>Statut</strong></TableCell>
              <TableCell><strong>Date création</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : paginatedPayments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body1" color="text.secondary">
                    {searchTerm || filterStatus !== 'all' || filterPlan !== 'all' 
                      ? 'Aucun paiement trouvé avec ces critères' 
                      : 'Aucun paiement enregistré'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedPayments.map((payment) => (
                <TableRow key={payment.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {payment.personsInfo}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {payment.contactInfo}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getPlanTypeIcon(payment.planType)}
                      <Typography variant="body2">
                        {payment.planType === 'custom' ? 'Personnalisé' : 'Formule'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {payment.displayPlanName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {getPaymentMethodIcon(payment.paymentMethod)}
                        <Typography variant="caption" color="text.secondary">
                          {payment.paymentMethod === 'bank' ? 'Virement' : 
                           payment.paymentMethod === 'cash' ? 'Espèces' :
                           payment.paymentMethod === 'check' ? 'Chèque' : 'Carte'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      {payment.totalAmount}€
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {payment.customCommunication || 'Aucune'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(payment.isPaid)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(payment.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Voir les détails">
                        <IconButton 
                          size="small" 
                          color="info"
                          onClick={() => handleViewDetails(payment)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {!payment.isPaid && (
                        <Tooltip title="Marquer comme payé">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleManualPayment(payment)}
                          >
                            <PaidIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!payment.isPaid && (
                        <Tooltip title={payment.email ? "Envoyer un rappel de paiement" : "Aucun email disponible"}>
                          <span>
                            <IconButton
                              size="small"
                              color="warning"
                              disabled={!payment.email}
                              onClick={() => handleOpenReminder(payment)}
                            >
                              <ReminderIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredPayments.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Lignes par page:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
          }
        />
      </TableContainer>

      {/* Dialog de détails */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Détails du paiement
        </DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Élève(s):</Typography>
                <Typography variant="body1">{selectedPayment.personsInfo}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Contact:</Typography>
                <Typography variant="body1">{selectedPayment.contactInfo}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Type:</Typography>
                <Typography variant="body1">
                  {selectedPayment.planType === 'custom' ? 'Montant personnalisé' : 'Formule prédéfinie'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Prestation:</Typography>
                <Typography variant="body1">{selectedPayment.displayPlanName}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Montant total:</Typography>
                <Typography variant="h6" color="primary">{selectedPayment.totalAmount}€</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Statut:</Typography>
                {getStatusChip(selectedPayment.isPaid)}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Communication:</Typography>
                <Typography variant="body1">
                  {selectedPayment.customCommunication || 'Aucune communication spécifique'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Méthode de paiement:</Typography>
                <Typography variant="body1">
                  {selectedPayment.paymentMethod === 'bank' ? 'Virement bancaire' :
                   selectedPayment.paymentMethod === 'cash' ? 'Espèces' :
                   selectedPayment.paymentMethod === 'check' ? 'Chèque' : 'Carte bancaire'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Date de création:</Typography>
                <Typography variant="body1">{formatDate(selectedPayment.createdAt)}</Typography>
              </Grid>
              {selectedPayment.isPaid && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Date de paiement:</Typography>
                  <Typography variant="body1">{formatDate(selectedPayment.paidAt)}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour marquer comme payé manuellement */}
      <Dialog open={manualPaymentDialogOpen} onClose={handleCloseManualPaymentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Marquer comme payé
        </DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="body1" gutterBottom>
                  Marquer le paiement de <strong>{selectedPayment.personsInfo}</strong> comme payé.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Montant: <strong>{selectedPayment.totalAmount}€</strong>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Date du paiement"
                  type="date"
                  value={manualPaymentData.paymentDate}
                  onChange={(e) => setManualPaymentData({
                    ...manualPaymentData,
                    paymentDate: e.target.value
                  })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Référence bancaire"
                  value={manualPaymentData.bankReference}
                  onChange={(e) => setManualPaymentData({
                    ...manualPaymentData,
                    bankReference: e.target.value
                  })}
                  placeholder="Ex: VIR123456, CHQ789012"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={manualPaymentData.notes}
                  onChange={(e) => setManualPaymentData({
                    ...manualPaymentData,
                    notes: e.target.value
                  })}
                  placeholder="Informations supplémentaires sur ce paiement..."
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseManualPaymentDialog}>Annuler</Button>
          <Button 
            variant="contained" 
            onClick={() => handleMarkAsPaid(
              selectedPayment?.id,
              manualPaymentData.bankReference,
              manualPaymentData.paymentDate,
              manualPaymentData.notes
            )}
          >
            Confirmer le paiement
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog d'envoi d'un rappel de paiement */}
      <Dialog open={reminderDialogOpen} onClose={handleCloseReminderDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Envoyer un rappel de paiement
        </DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="body1" gutterBottom>
                  Un email de rappel sera envoyé à <strong>{selectedPayment.email}</strong> pour le paiement de <strong>{selectedPayment.personsInfo}</strong>.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  L'email contiendra le montant dû ainsi que les coordonnées bancaires de l'école (IBAN, BIC) et la communication à utiliser.
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Montant:</Typography>
                <Typography variant="h6" color="primary">{selectedPayment.totalAmount}€</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Communication:</Typography>
                <Typography variant="body1">
                  {selectedPayment.customCommunication || selectedPayment.reference}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Coordonnées bancaires incluses:</Typography>
                <Typography variant="body2">{BANK_INFO.beneficiary}</Typography>
                <Typography variant="body2">IBAN: {BANK_INFO.iban}</Typography>
                <Typography variant="body2">BIC: {BANK_INFO.bic}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReminderDialog} disabled={sendingReminder}>Annuler</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSendReminder}
            disabled={sendingReminder}
            startIcon={sendingReminder ? <CircularProgress size={16} color="inherit" /> : <ReminderIcon />}
          >
            {sendingReminder ? 'Envoi en cours...' : "Envoyer le rappel"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de réconciliation */}
      <Dialog open={reconciliationDialogOpen} onClose={() => setReconciliationDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Réconciliation bancaire
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <input
              type="file"
              accept=".xlsx, .xls, .csv"
              onChange={handleFileUpload}
              style={{ marginBottom: '16px' }}
            />
            <Typography variant="body2" color="text.secondary">
              Importez votre relevé bancaire (Excel/CSV) pour réconcilier automatiquement les paiements.
            </Typography>
          </Box>
          
          {bankTransactions.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Transactions trouvées: {bankTransactions.length}
              </Typography>
              <Typography variant="body1" gutterBottom>
                Correspondances trouvées: {matchedTransactions.length}
              </Typography>
              
              {matchedTransactions.length > 0 && (
                <Button 
                  variant="contained" 
                  color="success" 
                  onClick={handleAutoReconcile}
                  sx={{ mb: 2 }}
                >
                  Réconcilier {matchedTransactions.length} paiements
                </Button>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReconciliationDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Payments;